// @ts-nocheck
import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapboxToken } from '@/hooks/useMapboxToken';
import { College, CollegeFilters } from '@/hooks/useCollegeSearch';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, ZoomIn, ZoomOut, Locate } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CollegeModal from './CollegeModal';

interface CollegeMapViewProps {
  filters: CollegeFilters;
  onToggleSave: (collegeId: string) => Promise<any>;
  fullScreen?: boolean;
}

interface CollegeWithCoords extends College {
  latitude: number;
  longitude: number;
}

// US state coordinates for quick navigation
const stateCoordinates: Record<string, { center: [number, number]; zoom: number }> = {
  'Alabama': { center: [-86.9023, 32.8067], zoom: 6 },
  'Alaska': { center: [-153.4937, 64.2008], zoom: 4 },
  'Arizona': { center: [-111.0937, 34.0489], zoom: 6 },
  'Arkansas': { center: [-92.3731, 34.9697], zoom: 6 },
  'California': { center: [-119.4179, 36.7783], zoom: 5 },
  'Colorado': { center: [-105.3111, 39.0598], zoom: 6 },
  'Connecticut': { center: [-72.7554, 41.6032], zoom: 8 },
  'Delaware': { center: [-75.5277, 39.1582], zoom: 8 },
  'Florida': { center: [-81.5158, 27.6648], zoom: 6 },
  'Georgia': { center: [-83.5002, 32.1656], zoom: 6 },
  'Hawaii': { center: [-155.5828, 19.8968], zoom: 6 },
  'Idaho': { center: [-114.7420, 44.0682], zoom: 5 },
  'Illinois': { center: [-89.3985, 40.6331], zoom: 6 },
  'Indiana': { center: [-86.1349, 40.2672], zoom: 6 },
  'Iowa': { center: [-93.0977, 41.8780], zoom: 6 },
  'Kansas': { center: [-98.4842, 39.0119], zoom: 6 },
  'Kentucky': { center: [-84.2700, 37.8393], zoom: 6 },
  'Louisiana': { center: [-92.1450, 30.9843], zoom: 6 },
  'Maine': { center: [-69.4455, 45.2538], zoom: 6 },
  'Maryland': { center: [-76.6413, 39.0458], zoom: 7 },
  'Massachusetts': { center: [-71.3824, 42.4072], zoom: 7 },
  'Michigan': { center: [-85.6024, 44.3148], zoom: 6 },
  'Minnesota': { center: [-94.6859, 46.7296], zoom: 5 },
  'Mississippi': { center: [-89.3985, 32.3547], zoom: 6 },
  'Missouri': { center: [-91.8318, 37.9643], zoom: 6 },
  'Montana': { center: [-110.3626, 46.8797], zoom: 5 },
  'Nebraska': { center: [-99.9018, 41.4925], zoom: 6 },
  'Nevada': { center: [-116.4194, 38.8026], zoom: 5 },
  'New Hampshire': { center: [-71.5724, 43.1939], zoom: 7 },
  'New Jersey': { center: [-74.4057, 40.0583], zoom: 7 },
  'New Mexico': { center: [-105.8701, 34.5199], zoom: 6 },
  'New York': { center: [-75.4999, 43.2994], zoom: 6 },
  'North Carolina': { center: [-79.0193, 35.7596], zoom: 6 },
  'North Dakota': { center: [-101.0020, 47.5515], zoom: 6 },
  'Ohio': { center: [-82.9071, 40.4173], zoom: 6 },
  'Oklahoma': { center: [-97.0929, 35.0078], zoom: 6 },
  'Oregon': { center: [-120.5542, 43.8041], zoom: 6 },
  'Pennsylvania': { center: [-77.1945, 41.2033], zoom: 6 },
  'Rhode Island': { center: [-71.4774, 41.5801], zoom: 9 },
  'South Carolina': { center: [-81.1637, 33.8361], zoom: 7 },
  'South Dakota': { center: [-99.9018, 43.9695], zoom: 6 },
  'Tennessee': { center: [-86.5804, 35.5175], zoom: 6 },
  'Texas': { center: [-99.9018, 31.9686], zoom: 5 },
  'Utah': { center: [-111.0937, 39.3210], zoom: 6 },
  'Vermont': { center: [-72.5778, 44.5588], zoom: 7 },
  'Virginia': { center: [-78.6569, 37.4316], zoom: 6 },
  'Washington': { center: [-120.7401, 47.7511], zoom: 6 },
  'West Virginia': { center: [-80.4549, 38.5976], zoom: 7 },
  'Wisconsin': { center: [-89.6165, 43.7844], zoom: 6 },
  'Wyoming': { center: [-107.2903, 43.0759], zoom: 6 },
};

const CollegeMapView: React.FC<CollegeMapViewProps> = ({ filters, onToggleSave, fullScreen = false }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  
  const { token, loading: tokenLoading, error: tokenError } = useMapboxToken();
  const [colleges, setColleges] = useState<CollegeWithCoords[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCollege, setSelectedCollege] = useState<College | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch colleges with coordinates
  const fetchColleges = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('colleges')
        .select('*')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      // Apply search query filter
      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        query = query.ilike('name', `%${filters.searchQuery.trim()}%`);
      }

      // Apply location filters
      if (filters.states.length > 0) {
        query = query.in('state', filters.states);
      }

      // Apply division filters
      if (filters.divisions.length > 0) {
        query = query.in('division', filters.divisions);
      }

      // Apply conference filters
      if (filters.conferences.length > 0) {
        query = query.in('conference', filters.conferences);
      }

      // Apply religious affiliation filters
      if (filters.religiousAffiliations.length > 0) {
        query = query.in('religious_affiliation', filters.religiousAffiliations);
      }

      // Apply tuition filters
      if (filters.tuitionMin !== null) {
        query = query.gte('out_state_tuition', filters.tuitionMin);
      }
      if (filters.tuitionMax !== null) {
        query = query.lte('out_state_tuition', filters.tuitionMax);
      }

      // Apply SAT score filters
      if (filters.satMin !== null) {
        query = query.gte('average_sat_score', filters.satMin);
      }
      if (filters.satMax !== null) {
        query = query.lte('average_sat_score', filters.satMax);
      }

      // Apply ACT score filters
      if (filters.actMin !== null) {
        query = query.gte('average_act_score', filters.actMin);
      }
      if (filters.actMax !== null) {
        query = query.lte('average_act_score', filters.actMax);
      }

      // Apply GPA filters
      if (filters.gpaMin !== null) {
        query = query.gte('average_gpa', filters.gpaMin);
      }
      if (filters.gpaMax !== null) {
        query = query.lte('average_gpa', filters.gpaMax);
      }

      console.log('🗺️ Map view applying filters:', filters);

      const { data, error } = await query;
      
      if (error) throw error;
      setColleges(data as CollegeWithCoords[]);
    } catch (err) {
      console.error('Error fetching colleges:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchColleges();
  }, [fetchColleges]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || !token || map.current) return;

    mapboxgl.accessToken = token;
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-98.5795, 39.8283], // Center of US
      zoom: 3.5,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, [token]);

  // Update markers when colleges change
  useEffect(() => {
    if (!map.current || !colleges.length) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add new markers
    colleges.forEach((college) => {
      if (!college.longitude || !college.latitude) return;

      // Create custom marker element
      const el = document.createElement('div');
      el.className = 'college-marker';
      el.style.width = '24px';
      el.style.height = '24px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = getDivisionColor(college.division);
      el.style.border = '2px solid white';
      el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      el.style.cursor = 'pointer';

      // Create popup for hover only (not attached to marker to avoid click issues)
      const popup = new mapboxgl.Popup({ 
        offset: 25, 
        closeButton: false,
        closeOnClick: false 
      }).setHTML(`
          <div style="padding: 8px; max-width: 200px;">
            <strong style="font-size: 14px;">${college.name}</strong>
            <p style="margin: 4px 0; font-size: 12px; color: #666;">${college.city}, ${college.state}</p>
            <p style="margin: 0; font-size: 11px; color: #888;">${college.division || 'N/A'}</p>
          </div>
        `);

      // Create marker WITHOUT popup attached (to prevent Mapbox click handling)
      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([college.longitude, college.latitude])
        .addTo(map.current!);

      // Show popup on hover only
      el.addEventListener('mouseenter', () => {
        popup.setLngLat([college.longitude, college.latitude]).addTo(map.current!);
      });
      el.addEventListener('mouseleave', () => {
        popup.remove();
      });

      // Click to open modal - completely separate from Mapbox
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        popup.remove();
        setSelectedCollege(college);
        setModalOpen(true);
      });

      markersRef.current.push(marker);
    });
  }, [colleges]);

  // Handle state selection
  const handleStateChange = (state: string) => {
    setSelectedState(state);
    if (state && stateCoordinates[state] && map.current) {
      map.current.flyTo({
        center: stateCoordinates[state].center,
        zoom: stateCoordinates[state].zoom,
        duration: 1500,
      });
    } else if (map.current) {
      // Reset to US view
      map.current.flyTo({
        center: [-98.5795, 39.8283],
        zoom: 3.5,
        duration: 1500,
      });
    }
  };

  const handleZoomIn = () => {
    map.current?.zoomIn();
  };

  const handleZoomOut = () => {
    map.current?.zoomOut();
  };

  const handleResetView = () => {
    map.current?.flyTo({
      center: [-98.5795, 39.8283],
      zoom: 3.5,
      duration: 1000,
    });
    setSelectedState('');
  };

  const handleSuggestCorrection = (collegeId: string) => {
    // For now, just close the modal
    setModalOpen(false);
  };

  if (tokenLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading map...</span>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="flex items-center justify-center h-96 text-destructive">
        <p>{tokenError}. Please ensure MAPBOX_PUBLIC_TOKEN is configured.</p>
      </div>
    );
  }

  return (
    <div className={`relative ${fullScreen ? 'h-screen' : 'h-[600px]'} w-full rounded-lg overflow-hidden`}>
      {/* Controls overlay */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
        <Select value={selectedState} onValueChange={handleStateChange}>
          <SelectTrigger className="w-48 bg-white shadow-md">
            <SelectValue placeholder="Jump to state..." />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All States</SelectItem>
            {Object.keys(stateCoordinates).sort().map((state) => (
              <SelectItem key={state} value={state}>
                {state}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex gap-1">
          <Button size="sm" variant="secondary" onClick={handleZoomIn} className="shadow-md">
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="secondary" onClick={handleZoomOut} className="shadow-md">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="secondary" onClick={handleResetView} className="shadow-md">
            <Locate className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-md">
        <p className="text-xs font-semibold mb-2">Divisions</p>
        <div className="flex flex-col gap-1">
          {[
            { label: 'NCAA D1', color: '#002E62' },
            { label: 'NCAA D2', color: '#BC8D09' },
            { label: 'NCAA D3', color: '#4CAF50' },
            { label: 'NAIA', color: '#9C27B0' },
            { label: 'NJCAA', color: '#FF5722' },
            { label: 'Other', color: '#607D8B' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* College count */}
      <div className="absolute top-4 right-16 z-10 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-md">
        <p className="text-sm font-medium">{colleges.length} colleges</p>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Map container */}
      <div ref={mapContainer} className="w-full h-full" />

      {/* College Modal */}
      {selectedCollege && (
        <CollegeModal
          college={selectedCollege}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedCollege(null);
          }}
          onToggleSave={onToggleSave}
          onSuggestCorrection={handleSuggestCorrection}
        />
      )}
    </div>
  );
};

// Helper function to get color by division
function getDivisionColor(division: string | null): string {
  switch (division) {
    case 'NCAA D1':
      return '#002E62'; // Primary blue
    case 'NCAA D2':
      return '#BC8D09'; // Secondary gold
    case 'NCAA D3':
      return '#4CAF50'; // Green
    case 'NAIA':
      return '#9C27B0'; // Purple
    case 'NJCAA':
      return '#FF5722'; // Orange
    default:
      return '#607D8B'; // Gray
  }
}

export default CollegeMapView;
